import { useEffect, useRef, useState, useMemo, useCallback } from 'react';
import DevConsole from './DevConsole';
import { Combobox } from '@headlessui/react';
import ForceGraph2D from 'react-force-graph-2d';
import Graph from 'graphology';
import louvain from 'graphology-communities-louvain';
import {
  forceSimulation,
  forceCollide,
} from 'd3-force';
import degreeDistanceForce from './degreeDistanceForce';
import sourceMap from './sourceMap';
import communityRepelForce from './communityRepelForce';
import communityCentroidForce from './communityCentroidForce';
import mainNodeAttractForce from './mainNodeAttractForce';
import createCommunityTwoPassTick from './communityTwoPassTick';


export default function App() {
  const [allNodes, setAllNodes] = useState([]);
  const [allLinks, setAllLinks] = useState([]);
  const data = useMemo(() => ({ nodes: allNodes, links: allLinks }), [allNodes, allLinks]);
  const allNodesRef = useRef([]);
  const nodeMapRef = useRef(new Map());
  const fullNodesRef = useRef([]);
  const fullLinksRef = useRef([]);
  const [_communityVirtualNodes, setCommunityVirtualNodes] = useState([]);
  const communityVirtualNodesRef = useRef([]);
  const simulationRef = useRef(null);
  const loadIndexRef = useRef(0);
  const BASE_LINK_DISTANCE = 30;
  const BASE_LINK_DISTANCE_OFFSET = 10;
  // Number of nodes to load at a time when building the initial graph
  const CHUNK_SIZE = 10;
  // how many chunks should load before the simulation ticks/progress updates
  const TICK_EVERY_CHUNKS = 10;
  const chunkCounterRef = useRef(0);
  const [sources, setSources] = useState([]);
  const [sourceStates, setSourceStates] = useState({}); // 0=neutral,1=include,2=exclude
  const [tags, setTags] = useState([]);
  const [tagStates, setTagStates] = useState({}); // 0=neutral,1=include,2=exclude
  const [selectedNode, setSelectedNode] = useState(null);
  const [mobilePaneOpen, setMobilePaneOpen] = useState(false);
  const fgRef = useRef();
  const containerRef = useRef(null);
  const topBarRef = useRef(null);
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 });
  const inputRef = useRef('');
  const [showConsole, setShowConsole] = useState(false);
  const [labelsVisible, setLabelsVisible] = useState(false);
  const [showNodeIds, setShowNodeIds] = useState(false);
  const [showSources, setShowSources] = useState(false);
  const [showTags, setShowTags] = useState(false);
  const [loading, setLoading] = useState(true);
  const [progress, setProgress] = useState({ loaded: 0, total: 0 });
  const [graphKey, setGraphKey] = useState(0);
  const collisionEnabled = false;
  const [hoverNode, setHoverNode] = useState(null);
  const [markers, setMarkers] = useState([]);

  const activeSourceCount = useMemo(
    () => Object.values(sourceStates).filter((v) => v > 0).length,
    [sourceStates],
  );
  const sourceToggleLabel =
    activeSourceCount > 0 ? `Sources (${activeSourceCount})` : 'Sources';

  const activeTagCount = useMemo(
    () => Object.values(tagStates).filter((v) => v > 0).length,
    [tagStates],
  );
  const tagToggleLabel = activeTagCount > 0 ? `Tags (${activeTagCount})` : 'Tags';

  const NODE_RADIUS = 10;
  const LINK_CURVATURE = 0.1;
  const ARROW_LENGTH = 5;
  const NODE_LABEL_COLOR = '#cbd5e1';
  const NODE_LABEL_STROKE_COLOR = '#000000';
  const NODE_LABEL_STROKE_WIDTH = 0.5;
  const COMMUNITY_DISTANCE_MULT = 2;
  const COMMUNITY_NODE_RADIUS = 6;
  const INTERCOMMUNITY_LINK_COLOR = '#f87171';
  const MARKER_LETTERS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
  const loadTimeoutRef = useRef(null);

  const [physicsSettings, setPhysicsSettings] = useState({
    timeStep: 0.5,
    degreeDistance: 300,
    communityRepel: 0.7,
    mainAttract: 0.5,
  });

  const updatePhysics = useCallback((key, value) => {
    setPhysicsSettings((prev) => ({ ...prev, [key]: value }));
    const sim = simulationRef.current;
    if (!sim) return;
    switch (key) {
      case 'degreeDistance':
        sim.force('degreeDist')?.distance(value);
        break;
      case 'communityRepel':
        sim.force('community')?.strength(value);
        break;
      case 'mainAttract':
        sim.force('mainAttract')?.strength(value);
        break;
      default:
        break;
    }
    sim.alpha(1);
  }, []);

  const colorForCommunity = useCallback((id) => {
    const hue = (id * 137.508) % 360;
    return `hsl(${hue},65%,60%)`;
  }, []);

  useEffect(() => {
    if (!loading) setGraphKey((k) => k + 1);
  }, [loading]);

  useEffect(() => {
    allNodesRef.current = allNodes;
  }, [allNodes]);

  useEffect(() => {
    const idxMap = new Map();
    markers.forEach((id, i) => {
      idxMap.set(id, i);
    });
    allNodes.forEach((n) => {
      n.markerIndex = idxMap.get(n.id);
    });
    if (fgRef.current && typeof fgRef.current.refresh === 'function') {
      fgRef.current.refresh();
    }
  }, [markers, allNodes]);

  const loadChunk = useCallback(() => {
    console.debug('loadChunk:start', {
      loaded: loadIndexRef.current,
      total: fullNodesRef.current.length,
    });
    const start = loadIndexRef.current;
    const end = Math.min(start + CHUNK_SIZE, fullNodesRef.current.length);
    loadIndexRef.current = end;
    console.debug('loadChunk', { start, end });

    const existingMap = new Map(allNodesRef.current.map((n) => [n.id, n]));
    const combined = [...allNodesRef.current];
    const existingIds = new Set(existingMap.keys());
    for (let i = start; i < end; i++) {
      const node = fullNodesRef.current[i];
      let nearest = null;
      for (const l of fullLinksRef.current) {
        const s = typeof l.source === 'object' ? l.source.id : l.source;
        const t = typeof l.target === 'object' ? l.target.id : l.target;
        if (s === node.id && existingIds.has(t)) {
          const d = (l.distance || BASE_LINK_DISTANCE) + BASE_LINK_DISTANCE_OFFSET;
          if (!nearest || d < nearest.d) nearest = { id: t, d };
        } else if (t === node.id && existingIds.has(s)) {
          const d = (l.distance || BASE_LINK_DISTANCE) + BASE_LINK_DISTANCE_OFFSET;
          if (!nearest || d < nearest.d) nearest = { id: s, d };
        }
      }
      if (nearest) {
        const nb = existingMap.get(nearest.id);
        if (nb && nb.x !== undefined) {
          const jitter = 1;
          node.x = nb.x + (Math.random() - 0.5) * jitter;
          node.y = nb.y + (Math.random() - 0.5) * jitter;
          node.z = nb.z + (Math.random() - 0.5) * jitter;
        }
      }
      combined.push(node);
    }

    setAllNodes(() => {
      allNodesRef.current = combined;
      nodeMapRef.current = new Map(combined.map((n) => [n.id, n]));
      console.debug('loadChunk:setAllNodes', { count: combined.length });
      return combined;
    });
    chunkCounterRef.current += 1;
    if (chunkCounterRef.current % TICK_EVERY_CHUNKS === 0 || end === fullNodesRef.current.length) {
      setProgress({ loaded: end, total: fullNodesRef.current.length });
    }

    const ids = new Set(combined.map((n) => n.id));
    const newLinks = fullLinksRef.current
      .filter((l) => {
        const s = typeof l.source === 'object' ? l.source.id : l.source;
        const t = typeof l.target === 'object' ? l.target.id : l.target;
        return ids.has(s) && ids.has(t);
      })
      .map((l) => ({
        source: typeof l.source === 'object' ? l.source.id : l.source,
        target: typeof l.target === 'object' ? l.target.id : l.target,
        label: l.label,
        interCommunity: l.interCommunity,
      }));
    setAllLinks(newLinks);
    console.debug('loadChunk:setAllLinks', { count: newLinks.length });
    if (fgRef.current && typeof fgRef.current.refresh === 'function') {
      fgRef.current.refresh();
    }

    const srcSet = new Set();
    const tagCounts = new Map();
    for (const n of combined) {
      if (n.source) srcSet.add(n.source);
      if (Array.isArray(n.tags)) {
        for (const t of n.tags) {
          tagCounts.set(t, (tagCounts.get(t) || 0) + 1);
        }
      }
    }
    setSources([...srcSet].sort());
    setSourceStates((prev) => {
      let changed = false;
      const next = { ...prev };
      for (const s of srcSet) {
        if (!(s in next)) {
          next[s] = 0;
          changed = true;
        }
      }
      return changed ? next : prev;
    });

    const filteredTags = [...tagCounts.entries()]
      .filter(([, c]) => c > 1)
      .map(([t]) => t)
      .sort();
    setTags(filteredTags);
    setTagStates((prev) => {
      const next = {};
      for (const t of filteredTags) {
        next[t] = prev[t] || 0;
      }
      return next;
    });

    if (end < fullNodesRef.current.length) {
      loadTimeoutRef.current = setTimeout(loadChunk, 0);
    } else if (fgRef.current) {
      console.debug('all chunks loaded, zooming to fit');
      fgRef.current.zoomToFit(400);
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    return () => {
      if (loadTimeoutRef.current) {
        clearTimeout(loadTimeoutRef.current);
      }
    };
  }, []);

  
  useEffect(() => {
    const update = () => {
      if (containerRef.current) {
        const barHeight = topBarRef.current?.offsetHeight || 0;
        setDimensions({
          width: containerRef.current.offsetWidth,
          height: containerRef.current.offsetHeight - barHeight,
        });
      }
    };

    update();

    const ro = new ResizeObserver(update);
    if (containerRef.current) ro.observe(containerRef.current);

    window.addEventListener('resize', update);

    return () => {
      window.removeEventListener('resize', update);
      ro.disconnect();
    };
  }, []);
  useEffect(() => {
    setLoading(true);

    fetch('./data/latest.json')
      .then((r) => r.json())
      .then((d) => {
        const nodes = d.graph.nodes.map((n) => ({
          id: n.id,
          label: n.label,
          source: n.data?.source,
          tags: n.tags || [],
          entries: n.entries || '',
        }));
        let links = [];
        if (Array.isArray(d.graph.edges[0])) {
          links = d.graph.edges.map(([s, t, kind]) => ({
            source: d.graph.nodes[s].id,
            target: d.graph.nodes[t].id,
            label: kind,
          }));
        } else {
          links = d.graph.edges.map((e) => ({
            source: e.from,
            target: e.to,
            label: e.kind,
          }));
        }

        const buildScaled = (ns, ls) => {
          const adj = new Map();
          for (const l of ls) {
            const s = typeof l.source === 'object' ? l.source.id : l.source;
            const t = typeof l.target === 'object' ? l.target.id : l.target;
            if (!adj.has(s)) adj.set(s, new Set());
            if (!adj.has(t)) adj.set(t, new Set());
            adj.get(s).add(t);
            adj.get(t).add(s);
          }
          const result = ns.map((n) => {
            const degree = adj.get(n.id)?.size || 0;
            let scale;
            if (n.members) {
              scale = Math.sqrt(n.members.length);
            } else {
              scale = Math.sqrt(Math.max(degree, 2) / 2);
            }
            const r = NODE_RADIUS * scale;
            return {
              ...n,
              radius: r,
              val: Math.pow(r, 3),
              degree,
              boundaryLayer: n.boundaryLayer || 0,
              mass: r,
            };
          });
          return result;
        };

        const scaledNodes = buildScaled(nodes, links);

        const idToNode = new Map();
        scaledNodes.forEach((n) => idToNode.set(n.id, n));

        // replace link endpoints with node objects for easier sub-simulation
        links.forEach((l) => {
          if (typeof l.source !== 'object') l.source = idToNode.get(l.source);
          if (typeof l.target !== 'object') l.target = idToNode.get(l.target);
        });

        const nodeIndex = new Map();
        scaledNodes.forEach((n, i) => nodeIndex.set(n.id, i));

        const G = new Graph({ type: 'undirected' });
        for (const n of scaledNodes) {
          G.addNode(n.id);
        }
        for (const l of links) {
          const s = typeof l.source === 'object' ? l.source.id : l.source;
          const t = typeof l.target === 'object' ? l.target.id : l.target;
          if (s !== t && !G.hasEdge(s, t)) G.addEdge(s, t);
        }

        const result = louvain.detailed(G);
        const dendro = result.dendrogram.map((d) => Array.from(d));
        const communities = result.communities;
        const passes = Math.min(4, dendro.length - 1);

        const boundaryLayer = new Array(scaledNodes.length).fill(0);
        for (let lvl = 0; lvl < passes; lvl++) {
          const before = dendro[lvl];
          const after = dendro[lvl + 1];
          links.forEach((l) => {
            const si = nodeIndex.get(typeof l.source === 'object' ? l.source.id : l.source);
            const ti = nodeIndex.get(typeof l.target === 'object' ? l.target.id : l.target);
            if (before[si] !== before[ti] && after[si] === after[ti]) {
              boundaryLayer[si] = Math.max(boundaryLayer[si], lvl + 1);
              boundaryLayer[ti] = Math.max(boundaryLayer[ti], lvl + 1);
            }
          });
        }
        scaledNodes.forEach((n, i) => {
          n.boundaryLayer = boundaryLayer[i];
          n.community = communities[n.id];
          n.color = colorForCommunity(n.community);
        });

        links.forEach((l) => {
          const si = nodeIndex.get(typeof l.source === 'object' ? l.source.id : l.source);
          const ti = nodeIndex.get(typeof l.target === 'object' ? l.target.id : l.target);
          const maxLayer = Math.max(
            scaledNodes[si].boundaryLayer,
            scaledNodes[ti].boundaryLayer
          );
          const nodeRadiusSum =
            scaledNodes[si].radius + scaledNodes[ti].radius;
          l.distance =
            Math.pow(maxLayer + 1, 2) * BASE_LINK_DISTANCE *
            (maxLayer > 0 ? COMMUNITY_DISTANCE_MULT : 1) +
            nodeRadiusSum +
            BASE_LINK_DISTANCE_OFFSET;
          l.interCommunity =
            scaledNodes[si].community !== scaledNodes[ti].community &&
            (scaledNodes[si].boundaryLayer > 0 || scaledNodes[ti].boundaryLayer > 0);
        });

        const communityIds = new Set(scaledNodes.map((n) => n.community));
        const communityMass = new Map();
        const largestNode = new Map();
        for (const n of scaledNodes) {
          const m = communityMass.get(n.community) || 0;
          communityMass.set(n.community, m + (n.radius || NODE_RADIUS));
          if (!n.virtual) {
            const cur = largestNode.get(n.community);
            if (!cur || (n.radius || NODE_RADIUS) > (cur.radius || NODE_RADIUS)) {
              largestNode.set(n.community, n);
            }
          }
        }

        const communityNodes = new Map();
        for (const c of communityIds) {
          const vn = {
            id: `community-${c}`,
            community: c,
            virtual: true,
            radius: COMMUNITY_NODE_RADIUS,
            mass: communityMass.get(c) || 0,
            color: colorForCommunity(c),
            members: [],
          };
          communityNodes.set(c, vn);
        }

        for (const n of scaledNodes) {
          const vn = communityNodes.get(n.community);
          if (vn) vn.members.push(n);
        }

        const communityLinks = new Map();
        for (const l of links) {
          const sc = l.source.community;
          const tc = l.target.community;
          if (sc === tc) {
            if (!communityLinks.has(sc)) communityLinks.set(sc, []);
            communityLinks.get(sc).push(l);
          }
        }

        for (const vn of communityNodes.values()) {
          scaledNodes.push(vn);
        }

        const communityArray = Array.from(communityNodes.values());
        communityVirtualNodesRef.current = communityArray;
        setCommunityVirtualNodes(communityArray);

        // track the largest node per community
        for (const [c, node] of largestNode.entries()) {
          const vn = communityNodes.get(c);
          if (vn) vn.mainNode = node;
        }

        // compute hierarchy levels within each community
        for (const [cid, vn] of communityNodes.entries()) {
          const rootNode = vn.mainNode;
          if (!rootNode) continue;
          const commLinks = communityLinks.get(cid) || [];
          const adj = new Map();
          for (const l of commLinks) {
            const s = l.source;
            const t = l.target;
            if (!adj.has(s)) adj.set(s, new Set());
            if (!adj.has(t)) adj.set(t, new Set());
            adj.get(s).add(t);
            adj.get(t).add(s);
          }
          const queue = [rootNode];
          const levels = new Map([[rootNode, 0]]);
          while (queue.length) {
            const n = queue.shift();
            const lvl = levels.get(n);
            for (const nb of adj.get(n) || []) {
              if (!levels.has(nb)) {
                levels.set(nb, lvl + 1);
                queue.push(nb);
              }
            }
          }
          let maxLevel = 0;
          for (const [node, lvl] of levels) {
            node.hierLevel = lvl;
            if (lvl > maxLevel) maxLevel = lvl;
          }
          vn.maxHierarchy = maxLevel;
        }

        // link each node to its community virtual node for quick access
        for (const n of scaledNodes) {
          n.virtualNode = communityNodes.get(n.community) || null;
        }

        const communitiesData = Array.from(communityNodes.values()).map((vn) => ({
          id: vn.community,
          vn,
          nodes: vn.members.filter((m) => !m.virtual),
          links: communityLinks.get(vn.community) || [],
        }));

        const sim = forceSimulation(scaledNodes)
          .force(
            'degreeDist',
            degreeDistanceForce(physicsSettings.degreeDistance).links(links),
          )
          .force(
            'nodeCollide',
            forceCollide().radius((n) => (n.radius || NODE_RADIUS) * 1.5),
          )
          .force('collide', forceCollide().radius(0))
          .force('centroid', communityCentroidForce())
          .force('mainAttract', mainNodeAttractForce(physicsSettings.mainAttract))
          .force('community', communityRepelForce(physicsSettings.communityRepel));
        sim.stop();
        sim.tick = createCommunityTwoPassTick(
          sim,
          communitiesData,
          ['centroid', 'community', 'mainAttract'],
          ['degreeDist', 'nodeCollide'],
        );
        simulationRef.current = sim;

        setLabelsVisible(false);
        fullNodesRef.current = scaledNodes;
        fullLinksRef.current = links;
        setProgress({ loaded: 0, total: scaledNodes.length });
        chunkCounterRef.current = 0;
        loadIndexRef.current = 0;
        if (loadTimeoutRef.current) {
          clearTimeout(loadTimeoutRef.current);
          loadTimeoutRef.current = null;
        }
        setAllNodes(() => {
          allNodesRef.current = [];
          nodeMapRef.current = new Map();
          console.debug('reset:setAllNodes');
          return [];
        });
        setAllLinks([]);
        console.debug('reset:setAllLinks');
        setLabelsVisible(true);
        loadChunk();
      })
      .finally(() => {
      });
  }, [loadChunk]);

  const [search, setSearch] = useState('');
  const [suggestions, setSuggestions] = useState([]);

  const updateFilteredNodes = useCallback(() => {
    console.debug('updateFilteredNodes');
    const include = Object.entries(sourceStates)
      .filter(([, st]) => st === 1)
      .map(([s]) => s);
    const exclude = new Set(
      Object.entries(sourceStates)
        .filter(([, st]) => st === 2)
        .map(([s]) => s),
    );
    const includeTags = Object.entries(tagStates)
      .filter(([, st]) => st === 1)
      .map(([t]) => t);
    const excludeTags = new Set(
      Object.entries(tagStates)
        .filter(([, st]) => st === 2)
        .map(([t]) => t),
    );
    const includeSet = new Set(include);
    const filterActive = include.length > 0;
    const tagIncludeSet = new Set(includeTags);
    const tagFilterActive = includeTags.length > 0;
    const visibleIds = new Set();
    for (const n of allNodes) {
      let visible = true;
      if (exclude.has(n.source)) {
        visible = false;
      } else if (filterActive && !includeSet.has(n.source)) {
        visible = false;
      }
      if (visible) {
        const nodeTags = Array.isArray(n.tags) ? n.tags : [];
        if (nodeTags.some((t) => excludeTags.has(t))) {
          visible = false;
        } else if (tagFilterActive && !nodeTags.some((t) => tagIncludeSet.has(t))) {
          visible = false;
        }
      }
      n.__visible = visible;
      if (visible) visibleIds.add(n.id);
    }
    const selId = selectedNode ? selectedNode.id : null;
    for (const l of allLinks) {
      const s = typeof l.source === 'object' ? l.source.id : l.source;
      const t = typeof l.target === 'object' ? l.target.id : l.target;
      const baseVisible = visibleIds.has(s) && visibleIds.has(t);
      let selVisible;
      if (l.interCommunity) {
        selVisible = selId ? (s === selId || t === selId) : false;
      } else {
        selVisible = !selId || s === selId || t === selId;
      }
      l.__visible = baseVisible && selVisible;
    }
      if (fgRef.current && typeof fgRef.current.refresh === 'function') {
        fgRef.current.refresh();
        console.debug('updateFilteredNodes:refreshed');
      }
  }, [allNodes, allLinks, sourceStates, tagStates, labelsVisible, selectedNode]);


  useEffect(() => {
    updateFilteredNodes();
  }, [allNodes, allLinks, sourceStates, tagStates, labelsVisible, selectedNode]);

  const updateCollisionRadius = useCallback(() => {
    if (!collisionEnabled || !simulationRef.current) return;
    const collide = simulationRef.current.force('collide');
    if (!collide) return;
    const a = Math.max(simulationRef.current.alpha(), 0.001);
    console.log('collision alpha', a);
    collide
      .radius((n) => {
        if (n.virtual) return 0;
        if (n.degree <= 2) return 0;
        const base = (n.radius || NODE_RADIUS) * 1.5 +
          2 * (n.radius || NODE_RADIUS);
        if (a > 0.5) return 0;
        const blend = (0.5 - a) / 0.5;
        return base * blend;
      })
      .initialize(allNodesRef.current);
  }, []);


  useEffect(() => {
    let frame;
    const tick = () => {
      const sim = simulationRef.current;
      if (sim && (!loading || chunkCounterRef.current >= TICK_EVERY_CHUNKS)) {
        for (let i = 0; i < Math.max(1, Math.round(physicsSettings.timeStep)); i++) {
          sim.tick();
        }
        // Reheat the simulation so it never cools down
        sim.alpha(1);
        if (!loading) {
          updateCollisionRadius();
        }
        if (fgRef.current && typeof fgRef.current.refresh === 'function') {
          fgRef.current.refresh();
        }
        if (loading) {
          chunkCounterRef.current = 0;
        }
      }
      frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [updateCollisionRadius, physicsSettings.timeStep, loading]);






  useEffect(() => {
    if (loading) {
      document.body.classList.add('overflow-hidden');
    } else {
      document.body.classList.remove('overflow-hidden');
    }
  }, [loading]);
  const onChange = (e) => {
    const value = e.target.value;
    inputRef.current = value.trim();
    setSearch(value);
    if (value) {
      const val = value.toLowerCase();
      setSuggestions(
        data.nodes
          .filter((n) => {
            if (n.virtual) return false;
            const labelMatch = n.label?.toLowerCase().includes(val);
            const tagMatch = n.tags.some((t) => t.toLowerCase().includes(val));
            const entryMatch =
              typeof n.entries === 'string' &&
              n.entries.toLowerCase().includes(val);
            return labelMatch || tagMatch || entryMatch;
          })
          .slice(0, 10),
      );
    } else {
      setSuggestions([]);
    }
  };
  const jumpToLabel = (label) => {
    const node = data.nodes.find((n) => n.label === label);
    if (node && fgRef.current) {
      console.debug('jumpToLabel', label);
      setSelectedNode(node);
      fgRef.current.centerAt(node.x, node.y, 1000);
      fgRef.current.zoom(4, 1000);
    }
  };

  const onKeyDown = (e) => {
    if (e.key === 'Enter') {
      jumpToLabel(inputRef.current || e.target.value.trim());
    }
  };

  const toggleSourceState = (s) => {
    setSourceStates((prev) => {
      const next = ((prev[s] || 0) + 1) % 3;
      return { ...prev, [s]: next };
    });
  };

  const toggleTagState = (t) => {
    setTagStates((prev) => {
      const next = ((prev[t] || 0) + 1) % 3;
      return { ...prev, [t]: next };
    });
  };

  const ignoreSourceOfSelected = () => {
    if (selectedNode && selectedNode.source) {
      console.debug('ignoreSourceOfSelected', selectedNode.source);
      setSourceStates((prev) => ({ ...prev, [selectedNode.source]: 2 }));
      setSelectedNode(null);
    }
  };

  const reheatGraph = useCallback(() => {
    if (simulationRef.current) {
      simulationRef.current.alpha(1);
    }
  }, []);

  const onNodeDrag = useCallback((node, translate) => {
    console.debug('onNodeDrag', {
      id: node.id,
      translate,
    });
  }, []);

  const onNodeDragEnd = useCallback((node) => {
    console.debug('onNodeDragEnd', {
      id: node.id,
      fx: node.fx,
      fy: node.fy,
    });
  }, []);

  const onBackgroundClick = useCallback(() => {
    console.debug('onBackgroundClick');
    setSelectedNode(null);
  }, []);

  const DEFAULT_LINK_OPACITY = 0.4;
  const HIGHLIGHT_LINK_OPACITY = 0.8;
  const DIM_LINK_OPACITY = 0.1;
  const DIM_NODE_OPACITY = 0.25;

  const nodeCanvasObject = useCallback(
    (node, ctx, globalScale) => {
      if (!node.__visible) return;
      if (node.virtual) return; // drawn in onRenderFramePost
      const radius = node.radius || NODE_RADIUS;
      const isShadow = !ctx.canvas.isConnected;
      if (!isShadow) {
        ctx.globalAlpha = node.__opacity ?? 1;
        ctx.fillStyle = node.color || '#f3f4f6';
      }
      ctx.beginPath();
      ctx.arc(node.x, node.y, radius, 0, 2 * Math.PI);
      ctx.fill();

      if (!isShadow) {
        ctx.lineWidth = 2 / globalScale;
        ctx.strokeStyle = '#374151';
        ctx.beginPath();
        ctx.arc(node.x, node.y, radius + 2, 0, 2 * Math.PI);
        ctx.stroke();
        ctx.globalAlpha = 1;
      }

    const showLabel = !node.virtual && (labelsVisible || selectedNode?.id === node.id || hoverNode?.id === node.id);
    if (showLabel) {
      const fontSize = (selectedNode ? 16 : 8) / globalScale;
      ctx.font = `${fontSize}px sans-serif`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'top';
      ctx.lineWidth = NODE_LABEL_STROKE_WIDTH / globalScale;
      ctx.strokeStyle = NODE_LABEL_STROKE_COLOR;
      ctx.fillStyle = NODE_LABEL_COLOR;
      const startY = node.y + radius + 2 / globalScale;
      const lineHeight = fontSize + 2 / globalScale;
      const lines = showNodeIds ? [node.label, node.id] : [node.label];
      lines.forEach((text, i) => {
        const y = startY + i * lineHeight;
        ctx.strokeText(text, node.x, y);
        ctx.fillText(text, node.x, y);
      });
    }

    if (node.markerIndex !== undefined) {
      const mark = MARKER_LETTERS[node.markerIndex] || '?';
      const mr = 6 / globalScale;
      const offset = radius + mr;
      const mx = node.x + offset * 0.7;
      const my = node.y - offset * 0.7;
      ctx.fillStyle = '#dc2626';
      ctx.beginPath();
      ctx.arc(mx, my, mr, 0, 2 * Math.PI);
      ctx.fill();
      ctx.fillStyle = '#ffffff';
      ctx.font = `${8 / globalScale}px sans-serif`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(mark, mx, my);
    }
  },
  [labelsVisible, selectedNode, hoverNode, showNodeIds],
);

  const nodePointerAreaPaint = useCallback((node, color, ctx) => {
    if (!node.__visible || node.virtual) return;
    const radius = (node.radius || NODE_RADIUS) + 3;
    ctx.fillStyle = color;
    ctx.beginPath();
    ctx.arc(node.x, node.y, radius, 0, 2 * Math.PI);
    ctx.fill();
  }, []);

  const linkPointerAreaPaint = useCallback((link, color, ctx) => {
    if (!link.__visible) return;
    const start = link.source;
    const end = link.target;
    const startRadius = start.radius || NODE_RADIUS;
    const endRadius = end.radius || NODE_RADIUS;
    const dirX = end.x - start.x;
    const dirY = end.y - start.y;
    const len = Math.hypot(dirX, dirY);
    if (!len) return;
    const normX = dirX / len;
    const normY = dirY / len;
    const offsetStartX = start.x + normX * startRadius;
    const offsetStartY = start.y + normY * startRadius;
    const offsetEndX = end.x - normX * endRadius;
    const offsetEndY = end.y - normY * endRadius;
    ctx.strokeStyle = color;
    ctx.lineWidth = 5;
    ctx.beginPath();
    if (LINK_CURVATURE) {
      const midX = (offsetStartX + offsetEndX) / 2;
      const midY = (offsetStartY + offsetEndY) / 2;
      const cpX = midX - normY * LINK_CURVATURE * len;
      const cpY = midY + normX * LINK_CURVATURE * len;
      ctx.moveTo(offsetStartX, offsetStartY);
      ctx.quadraticCurveTo(cpX, cpY, offsetEndX, offsetEndY);
    } else {
      ctx.moveTo(offsetStartX, offsetStartY);
      ctx.lineTo(offsetEndX, offsetEndY);
    }
    ctx.stroke();
  }, []);

  const recomputeVirtualNodes = useCallback(() => {
    const alpha =
      fgRef.current && typeof fgRef.current.d3Alpha === 'function'
        ? fgRef.current.d3Alpha()
        : 0;
    if (alpha <= 0) return;
    const nodes = allNodesRef.current || [];
    const accum = new Map();
    for (const n of nodes) {
      if (n.virtual) continue;
      const vn = n.virtualNode;
      if (!vn) continue;
      if (!accum.has(vn)) accum.set(vn, { x: 0, y: 0, w: 0 });
      const w = n.radius || 1;
      const a = accum.get(vn);
      a.x += (n.x || 0) * w;
      a.y += (n.y || 0) * w;
      a.w += w;
    }
    for (const [vn, a] of accum) {
      if (!a.w) continue;
      vn.x = a.x / a.w;
      vn.y = a.y / a.w;
      vn.mass = a.w;
    }
  }, []);


  const drawVirtualCentroids = useCallback(
    (ctx, globalScale) => {
      if (!ctx.canvas.isConnected) return; // avoid drawing on picking canvas
      recomputeVirtualNodes();
      const nodes = allNodesRef.current || [];
      for (const n of nodes) {
        if (!n.__visible || !n.virtual) continue;
        if (n.mainNode && n.mainNode.__visible) {
          ctx.strokeStyle = n.color || '#f3f4f6';
          ctx.lineWidth = 1 / globalScale;
          ctx.beginPath();
          ctx.moveTo(n.mainNode.x, n.mainNode.y);
          ctx.lineTo(n.x, n.y);
          ctx.stroke();
        }
        const radius = n.radius || NODE_RADIUS;
        ctx.fillStyle = n.color || '#f3f4f6';
        ctx.beginPath();
        ctx.arc(n.x, n.y, radius, 0, 2 * Math.PI);
        ctx.fill();
        ctx.lineWidth = 2 / globalScale;
        ctx.strokeStyle = '#dc2626';
        ctx.beginPath();
        ctx.arc(n.x, n.y, radius + 2, 0, 2 * Math.PI);
        ctx.stroke();
      }
    },
    [],
  );

  const linkCanvasObject = useCallback(
    (link, ctx, globalScale) => {
      if (!link.__visible) return;
      const isShadow = !ctx.canvas.isConnected;
      if (isShadow) return; // skip drawing on shadow canvas to avoid pick color pollution
      const start = link.source;
      const end = link.target;
      const startRadius = start.radius || NODE_RADIUS;
      const endRadius = end.radius || NODE_RADIUS;
      const dirX = end.x - start.x;
      const dirY = end.y - start.y;
      const len = Math.hypot(dirX, dirY);
      if (!len) return;
      const normX = dirX / len;
      const normY = dirY / len;
      const offsetStartX = start.x + normX * startRadius;
      const offsetStartY = start.y + normY * startRadius;
      const offsetEndX = end.x - normX * endRadius;
      const offsetEndY = end.y - normY * endRadius;
      const crossCommunity =
        start.community !== end.community &&
        (start.boundaryLayer > 0 || end.boundaryLayer > 0);
      const linkColor = crossCommunity ? INTERCOMMUNITY_LINK_COLOR : '#9ca3af';
      ctx.strokeStyle = linkColor;
      ctx.lineWidth = 1 / globalScale;
      ctx.globalAlpha = link.__opacity ?? DEFAULT_LINK_OPACITY;
      ctx.beginPath();
      if (LINK_CURVATURE) {
        const midX = (offsetStartX + offsetEndX) / 2;
        const midY = (offsetStartY + offsetEndY) / 2;
        const cpX = midX - normY * LINK_CURVATURE * len;
        const cpY = midY + normX * LINK_CURVATURE * len;
        ctx.moveTo(offsetStartX, offsetStartY);
        ctx.quadraticCurveTo(cpX, cpY, offsetEndX, offsetEndY);
      } else {
        ctx.moveTo(offsetStartX, offsetStartY);
        ctx.lineTo(offsetEndX, offsetEndY);
      }
      ctx.stroke();
      ctx.globalAlpha = 1;

      const arrowLen = ARROW_LENGTH;
      const arrowTailX = offsetEndX - normX * arrowLen;
      const arrowTailY = offsetEndY - normY * arrowLen;
      ctx.fillStyle = linkColor;
      ctx.beginPath();
      ctx.moveTo(offsetEndX, offsetEndY);
      ctx.lineTo(
        arrowTailX - normY * (arrowLen / 2),
        arrowTailY + normX * (arrowLen / 2),
      );
      ctx.lineTo(
        arrowTailX + normY * (arrowLen / 2),
        arrowTailY - normX * (arrowLen / 2),
      );
      ctx.closePath();
      ctx.fill();

      const labelX =
        (offsetStartX + offsetEndX) / 2 - normY * LINK_CURVATURE * len * 0.5;
      const labelY =
        (offsetStartY + offsetEndY) / 2 + normX * LINK_CURVATURE * len * 0.5;
      const selId = selectedNode ? selectedNode.id : null;
      const srcId = typeof start === 'object' ? start.id : start;
      const tgtId = typeof end === 'object' ? end.id : end;
      const showLabel = selId && (srcId === selId || tgtId === selId);
      if (showLabel) {
        ctx.fillStyle = '#d1d5db';
        ctx.font = `${(selectedNode ? 8 : 4) / globalScale}px sans-serif`;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText(link.label, labelX, labelY);
      }
    },
    [selectedNode],
  );

  const onNodeClick = useCallback(
    (node, evt) => {
      if (node.virtual) return;
      console.debug('onNodeClick', {
        id: node.id,
        selecting: !selectedNode || selectedNode.id !== node.id,
        pointer: { x: evt?.clientX, y: evt?.clientY },
      });
      const selecting = !selectedNode || selectedNode.id !== node.id;
      setSelectedNode(selecting ? node : null);
      if (selecting && fgRef.current) {
        fgRef.current.centerAt(node.x, node.y, 1000);
        fgRef.current.zoom(4, 1000);
        if (typeof fgRef.current.refresh === 'function') {
          fgRef.current.refresh();
        }
      }
    },
    [selectedNode],
  );

  const onNodeRightClick = useCallback(
    (node, evt) => {
      if (node.virtual) return;
      evt?.preventDefault();
      setMarkers((prev) => {
        const idx = prev.indexOf(node.id);
        if (idx >= 0) {
          const copy = prev.slice();
          copy.splice(idx, 1);
          return copy;
        }
        if (prev.length >= MARKER_LETTERS.length) return prev;
        return [...prev, node.id];
      });
    },
    [],
  );

  const neighborNodes = useMemo(() => {
    if (!selectedNode) return [];
    const uniq = new Set();
    return data.links
      .filter((l) => {
        const srcId = typeof l.source === 'object' ? l.source.id : l.source;
        const tgtId = typeof l.target === 'object' ? l.target.id : l.target;
        return srcId === selectedNode.id || tgtId === selectedNode.id;
      })
      .map((l) => {
        const srcId = typeof l.source === 'object' ? l.source.id : l.source;
        const tgtId = typeof l.target === 'object' ? l.target.id : l.target;
        const entryId = `${srcId}-${tgtId}-${l.label}`;
        if (uniq.has(entryId)) return null;
        uniq.add(entryId);
        const isOutgoing = srcId === selectedNode.id;
        const targetNode = isOutgoing
          ? typeof l.target === 'object'
            ? l.target
            : data.nodes.find((n) => n.id === l.target)
          : typeof l.source === 'object'
            ? l.source
            : data.nodes.find((n) => n.id === l.source);
        return { id: entryId, node: targetNode, kind: l.label, outgoing: isOutgoing };
      })
      .filter(Boolean);
  }, [selectedNode, data.links, data.nodes]);

  useEffect(() => {
    console.debug('selectedNode changed', selectedNode?.id);
    if (!selectedNode) {
      allNodes.forEach((n) => {
        n.__opacity = 1;
      });
      allLinks.forEach((l) => {
        l.__opacity = DEFAULT_LINK_OPACITY;
      });
      if (fgRef.current && typeof fgRef.current.refresh === 'function') {
        fgRef.current.refresh();
      }
      setMobilePaneOpen(false);
      return;
    }
    setMobilePaneOpen(true);
    const selId = selectedNode.id;
    const connected = new Set([selId]);
    allLinks.forEach((l) => {
      const s = typeof l.source === 'object' ? l.source.id : l.source;
      const t = typeof l.target === 'object' ? l.target.id : l.target;
      const linked = s === selId || t === selId;
      if (linked) {
        connected.add(s);
        connected.add(t);
      }
      l.__opacity = linked ? HIGHLIGHT_LINK_OPACITY : DIM_LINK_OPACITY;
    });
    allNodes.forEach((n) => {
      n.__opacity = connected.has(n.id) ? 1 : DIM_NODE_OPACITY;
    });
      if (fgRef.current && typeof fgRef.current.refresh === 'function') {
        fgRef.current.refresh();
      }
  }, [selectedNode, allNodes, allLinks]);



  // Pointer interactions handled by ForceGraph2D callbacks


  return (
    <>
      <div className="w-full h-full flex">
        <div className="graph-container flex-1" ref={containerRef}>
          <div ref={topBarRef}>
          <button
            type="button"
            className="p-2 text-sm border rounded-lg m-2 md:hidden"
            onClick={() => setMobilePaneOpen(true)}
            aria-controls="side-pane"
          >
            Menu
          </button>
        <Combobox
          value={search}
          onChange={(val) => {
            setSearch(val);
            setSuggestions([]);
            inputRef.current = val;
            jumpToLabel(val);
          }}
        >
          <div className="relative inline-block">
            <Combobox.Input
              placeholder="Search"
              className="py-1.5 px-2 m-2 block rounded-lg border-gray-700 bg-gray-800 text-sm text-gray-100 focus:border-blue-500 focus:ring-blue-500"
              onChange={onChange}
              onKeyDown={onKeyDown}
            />
            {suggestions.length > 0 && (
              <Combobox.Options className="absolute bg-gray-800 border border-gray-700 rounded-md shadow-md max-h-48 overflow-y-auto list-none p-0 w-full z-50">
                {suggestions.map((s) => (
                  <Combobox.Option
                    className={({ active }) =>
                      `px-2 py-1 cursor-pointer ${active ? 'bg-gray-700' : ''}`
                    }
                    key={s.id}
                    value={s.label}
                  >
                    {s.label}
                  </Combobox.Option>
                ))}
              </Combobox.Options>
            )}
          </div>
        </Combobox>
        <button
          type="button"
          className="p-2 text-sm border rounded-lg m-2"
          onClick={() => setShowSources((prev) => !prev)}
        >
          {sourceToggleLabel}
        </button>
        {showSources && (
          <div className="inline-block ml-4">
            {sources.map((s) => {
              const state = sourceStates[s] || 0;
              let cls = 'ml-1 inline-flex cursor-pointer items-center rounded-full border px-2 py-0.5 text-xs';
              if (state === 1) cls += ' border-green-700 bg-green-800 text-green-100';
              else if (state === 2) cls += ' border-red-700 bg-red-800 text-red-100';
              else cls += ' border-gray-700 bg-gray-800 text-gray-100';
              return (
                <span
                  key={s}
                  className={cls}
                  onClick={() => toggleSourceState(s)}
                  title={sourceMap[s] || s}
                >
                  {s}
                </span>
              );
            })}
          </div>
        )}
        <button
          type="button"
          className="p-2 text-sm border rounded-lg m-2"
          onClick={() => setShowTags((prev) => !prev)}
        >
          {tagToggleLabel}
        </button>
        {showTags && (
          <div className="inline-block ml-4">
            {tags.map((t) => {
              const state = tagStates[t] || 0;
              let cls = 'ml-1 inline-flex cursor-pointer items-center rounded-full border px-2 py-0.5 text-xs';
              if (state === 1) cls += ' border-green-700 bg-green-800 text-green-100';
              else if (state === 2) cls += ' border-red-700 bg-red-800 text-red-100';
              else cls += ' border-gray-700 bg-gray-800 text-gray-100';
              return (
                <span
                  key={t}
                  className={cls}
                  onClick={() => toggleTagState(t)}
                >
                  {t}
                </span>
              );
            })}
          </div>
        )}
          </div>
        <ForceGraph2D
          key={graphKey}
          ref={fgRef}
          graphData={data}
          nodeId="id"
          linkLabel="label"
          autoPauseRedraw={false}
          enablePointerInteraction={!loading}
          nodeCanvasObject={nodeCanvasObject}
          nodePointerAreaPaint={nodePointerAreaPaint}
          nodeVisibility={(n) => n.__visible}
          linkCanvasObject={linkCanvasObject}
          linkPointerAreaPaint={linkPointerAreaPaint}
          linkVisibility={(l) => l.__visible}
          linkCurvature={LINK_CURVATURE}
          onNodeHover={setHoverNode}
          onNodeClick={onNodeClick}
          onNodeRightClick={onNodeRightClick}
          onNodeDrag={onNodeDrag}
          onNodeDragEnd={onNodeDragEnd}
          onBackgroundClick={onBackgroundClick}
          onRenderFramePost={drawVirtualCentroids}
          onEngineStop={() => {}}
          cooldownTicks={0}
          width={dimensions.width}
          height={dimensions.height}
          backgroundColor="#111827"
          style={{ flexGrow: 1, width: '100%', visibility: loading ? 'hidden' : 'visible' }}
        />
        {loading && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-900 bg-opacity-90 overflow-hidden">
            Loading ({progress.loaded} / {progress.total})
          </div>
        )}
      </div>
      <div
        id="side-pane"
        className={`fixed md:static top-0 right-0 h-full w-80 transition-transform duration-300 bg-gray-800 text-gray-100 border-l border-gray-700 z-40 md:z-auto flex flex-col overflow-y-auto ${mobilePaneOpen ? 'translate-x-0' : 'translate-x-full'} md:translate-x-0 ${mobilePaneOpen ? '' : 'hidden'} md:flex`}
        tabIndex="-1"
      >
        <div className="flex justify-end md:hidden p-2">
          <button
            type="button"
            className="p-2 text-sm border rounded"
            onClick={() => setMobilePaneOpen(false)}
          >
            Close
          </button>
        </div>
        <div className="node-info p-4 space-y-2 border-b border-gray-700">
          {selectedNode ? (
            <>
              <h3 className="text-lg font-semibold">{selectedNode.label}</h3>
              <p className="text-sm text-gray-400 break-all">{selectedNode.id}</p>
              {selectedNode.tags && selectedNode.tags.length > 0 && (
                <div className="flex flex-wrap gap-1">
                  {selectedNode.tags.map((t) => (
                    <span
                      key={t}
                      className="inline-flex items-center rounded-full bg-gray-700 px-2 py-0.5 text-xs text-gray-100"
                    >
                      {t}
                    </span>
                  ))}
                </div>
              )}
              {selectedNode.entries && (
                <p className="text-sm whitespace-pre-line">{selectedNode.entries}</p>
              )}
              {selectedNode.source && (
                <div className="text-sm flex items-center gap-2">
                  <span>
                    Source: {sourceMap[selectedNode.source] || selectedNode.source}
                  </span>
                  <button
                    type="button"
                    className="ml-auto inline-flex items-center rounded border border-gray-700 bg-gray-800 px-2 py-0.5 text-xs hover:bg-gray-700"
                    onClick={ignoreSourceOfSelected}
                  >
                    Ignore
                  </button>
                </div>
              )}
            </>
          ) : (
            <p className="text-sm text-gray-400">Select a node</p>
          )}
        </div>
        <div className="neighbor-links flex-1 overflow-y-auto p-4">
          <h4 className="text-md font-semibold mb-2">Neighbors</h4>
          <ul className="space-y-1">
            {neighborNodes.map(({ id, node, kind, outgoing }) => {
              const readableKind = kind
                .replace(/([a-z])([A-Z])/g, '$1 $2')
                .toLowerCase();
              return (
                <li key={id}>
                  <a
                    className="text-blue-600 hover:text-blue-800 hover:underline"
                    href="#"
                    onClick={(e) => {
                      e.preventDefault();
                      jumpToLabel(node.label);
                    }}
                  >
                    {outgoing
                      ? `This ${readableKind} ${node.label}`
                      : `${node.label} ${readableKind} This`}
                  </a>
                </li>
              );
            })}
          </ul>
        </div>
        <div className="tools-section mt-4">
          <button
            type="button"
            className="hs-collapse-toggle py-1.5 px-3 m-2 inline-flex items-center gap-x-1 text-sm font-semibold rounded-lg border border-gray-700 bg-gray-800 hover:bg-gray-700"
            data-hs-collapse="#tools-collapse"
            aria-controls="tools-collapse"
          >
            Tools
            <svg
              className="hs-collapse-open:rotate-180 size-4 ml-1"
              xmlns="http://www.w3.org/2000/svg"
              width="16"
              height="16"
              fill="currentColor"
              viewBox="0 0 16 16"
            >
              <path fillRule="evenodd" d="M1.646 4.646a.5.5 0 0 1 .708 0L8 10.293l5.646-5.647a.5.5 0 0 1 .708.708l-6 6a.5.5 0 0 1-.708 0l-6-6a.5.5 0 0 1 0-.708z" />
            </svg>
          </button>
          <div id="tools-collapse" className="hs-collapse hidden w-full overflow-hidden transition-[height] duration-300">
            <div className="p-2 space-y-2">
              <button
                type="button"
                className="py-1.5 px-3 w-full text-sm font-semibold rounded-lg border border-gray-700 bg-gray-800 hover:bg-gray-700"
                onClick={() => setShowConsole(true)}
              >
                Open Dev Console
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
    {showConsole && (
      <DevConsole
        onClose={() => setShowConsole(false)}
        onReheat={reheatGraph}
        showIds={showNodeIds}
        setShowIds={setShowNodeIds}
        physics={physicsSettings}
        setPhysics={updatePhysics}
      />
    )}
    </>
  );
}
